import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type DatabaseMigrationProgress } from '@app/domain/ports';
import { DATABASE_SERVICE } from '@app/core/tokens/infrastructure.tokens';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from './notification.service';

export interface MigrationProgressToastData {
  progressPercent: number;
  total: number;
  completedCount: number;
  currentIndex: number;
  currentVersion: number;
  currentName: string;
}

@Injectable({ providedIn: 'root' })
export class MigrationProgressToastService {
  private readonly databaseService = inject(DATABASE_SERVICE);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly toastKey = 'migration-progress';

  private initialized = false;
  private isToastVisible = false;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly progressDataState = signal<MigrationProgressToastData | null>(null);
  readonly progressData = this.progressDataState.asReadonly();

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.databaseService.migrationProgress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((progress) => this.handleProgress(progress));
  }

  markToastHostReady(): void {
    // No-op: kept for API compatibility. The 400 ms delay is sufficient to ensure
    // the <p-toast> host is mounted before the toast is shown.
  }

  private handleProgress(progress: DatabaseMigrationProgress): void {
    switch (progress.status) {
      case 'queued':
        return;
      case 'running':
        this.progressDataState.set(this.toToastData(progress));
        if (!this.isToastVisible && this.showTimer === null) {
          // Delay showing the toast to avoid a flash for fast migrations and to
          // ensure the <p-toast> host is mounted before the first show call.
          this.showTimer = setTimeout(() => {
            this.showTimer = null;
            this.showToast();
          }, 400);
        }
        return;
      case 'completed':
        this.cancelPendingShow();
        this.resetDisplayState();
        this.notificationService.clear(this.toastKey);
        return;
      case 'failed':
        this.cancelPendingShow();
        this.resetDisplayState();
        this.notificationService.clear(this.toastKey);
        this.notificationService.error(this.buildFailureDetail(progress), this.buildFailureSummary(), {
          data: this.buildFailureTechnicalDetails(progress),
        });
        return;
    }
  }

  private showToast(): void {
    if (this.isToastVisible) {
      return;
    }

    this.notificationService.show({
      severity: 'info',
      key: this.toastKey,
      sticky: true,
      life: 0,
      closable: false,
      summary: this.translateService.instant('common.notifications.migration_progress_header'),
    });
    this.isToastVisible = true;
  }

  private cancelPendingShow(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private toToastData(progress: Extract<DatabaseMigrationProgress, { status: 'running' }>): MigrationProgressToastData {
    return {
      progressPercent: Math.round((progress.currentIndex / progress.total) * 100),
      total: progress.total,
      completedCount: progress.completedCount,
      currentIndex: progress.currentIndex,
      currentVersion: progress.currentVersion,
      currentName: progress.currentName,
    };
  }

  private resetDisplayState(): void {
    this.isToastVisible = false;
    this.progressDataState.set(null);
  }

  private buildFailureSummary(): string {
    return this.translateService.instant('common.notifications.migration_progress_failed_header');
  }

  private buildFailureDetail(progress: Extract<DatabaseMigrationProgress, { status: 'failed' }>): string {
    return this.translateService.instant('common.notifications.migration_progress_failed_message', {
      name: progress.currentName,
      version: progress.currentVersion,
      error: progress.errorMessage,
    });
  }

  private buildFailureTechnicalDetails(progress: Extract<DatabaseMigrationProgress, { status: 'failed' }>): string {
    return progress.errorDetails ?? progress.errorMessage;
  }
}
